// Phase 6B — Trainee Module Detail (single module view with classroom tabs).
//
// Tabs: Lesson · Notes · Q&A · Resources. Announcements + chat are P1
// (clearly labeled "Coming Soon" — no fake placeholders).
//
// If the module body is empty, trainees see the finalization message;
// admins see "CONTENT NEEDED" so they know to author it.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, FileText, MessageCircle, Link2, Lock, CheckCircle2, Save } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Markdown from "@/components/lifted/Markdown";

export default function ModuleDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [mod, setMod] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("lesson");

  const isAdmin = useMemo(
    () => !!(user?.roles?.includes("admin") || user?.roles?.includes("master_admin")),
    [user],
  );

  useEffect(() => {
    if (!user || user === false) return;
    api.get(`/training/official-modules/${slug}`)
      .then((r) => setMod(r.data.module))
      .catch((e) => { if (e?.response?.status !== 401) setErr(apiErrorMessage(e)); });
  }, [slug, user]);

  const markComplete = async () => {
    try {
      await api.post("/training/progress", { module_slug: slug, status: "complete", percent: 100 });
      setMod((m) => ({ ...m, progress: { status: "complete", percent: 100 } }));
    } catch (e) { console.warn("[training] markComplete failed", e); }
  };

  if (err) return <div className="glass-panel p-6 text-rose-300 text-sm">{err}</div>;
  if (!mod) return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="module-detail-loading">
      <div className="h-10 w-10 rounded-full border-2 border-lifted-green/30 border-t-lifted-green animate-spin" />
    </div>
  );

  const hasBody = !!(mod.body && mod.body.trim());

  return (
    <div className="space-y-6" data-testid={`module-detail-${mod.slug}`}>
      <Link to="/training" className="inline-flex items-center gap-1.5 text-xs text-lifted-dim hover:text-white">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Training
      </Link>

      <header className="space-y-2">
        <div className="font-bebas tracking-[0.22em] text-[0.65rem] text-lifted-green">
          MODULE {mod.order} · {mod.category}
        </div>
        <h1 className="font-anton text-3xl sm:text-4xl uppercase text-white leading-[0.95]">{mod.title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[0.65rem] tracking-[0.18em] font-bebas uppercase border rounded-full px-2 py-0.5 border-lifted-green/40 text-lifted-green">
            {mod.access?.toUpperCase()}
          </span>
          {mod.progress?.status === "complete" && (
            <span className="text-[0.65rem] tracking-[0.18em] font-bebas uppercase border rounded-full px-2 py-0.5 border-lifted-green/40 text-lifted-green inline-flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> COMPLETE
            </span>
          )}
          {!hasBody && isAdmin && (
            <span className="text-[0.65rem] tracking-[0.18em] font-bebas uppercase border rounded-full px-2 py-0.5 border-amber-400/50 text-amber-300">
              CONTENT NEEDED
            </span>
          )}
        </div>
      </header>

      {mod.hero_image_url && (
        <div className="rounded-2xl overflow-hidden border border-white/5">
          <img src={mod.hero_image_url} alt="" className="w-full max-h-[400px] object-cover" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" role="tablist">
        {[
          { v: "lesson",    icon: BookOpen,      label: "Lesson"    },
          { v: "notes",     icon: FileText,      label: "Notes"     },
          { v: "qa",        icon: MessageCircle, label: "Q&A"       },
          { v: "resources", icon: Link2,         label: "Resources" },
        ].map((t) => {
          const Ic = t.icon;
          const active = tab === t.v;
          return (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bebas tracking-[0.18em] ${
                active ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40" : "border-white/10 text-lifted-dim hover:text-white"
              }`}
              data-testid={`module-tab-${t.v}`}
            >
              <Ic className="h-3.5 w-3.5" /> {t.label.toUpperCase()}
            </button>
          );
        })}
      </div>

      {tab === "lesson"    && <LessonTab mod={mod} hasBody={hasBody} onComplete={markComplete} />}
      {tab === "notes"     && <NotesTab slug={mod.slug} />}
      {tab === "qa"        && <QATab slug={mod.slug} />}
      {tab === "resources" && <ResourcesTab mod={mod} />}
    </div>
  );
}

function LessonTab({ mod, hasBody, onComplete }) {
  return (
    <div className="glass-panel p-6 space-y-4" data-testid="module-tab-content-lesson">
      {hasBody ? (
        <Markdown source={mod.body} />
      ) : (
        <div className="text-center py-8">
          <Lock className="h-6 w-6 text-lifted-dim mx-auto mb-3" />
          <div className="font-bebas tracking-[0.22em] text-sm text-white">CONTENT FORTHCOMING</div>
          <p className="text-lifted-dim text-sm mt-2 max-w-md mx-auto">
            This module is being finalized by Lifted Training. Please check back soon.
            Subcategories below preview what's coming.
          </p>
        </div>
      )}

      {mod.subcategories && mod.subcategories.length > 0 && (
        <div className="border-t border-white/10 pt-4">
          <div className="font-bebas tracking-[0.22em] text-[0.65rem] text-lifted-green mb-2">SECTIONS</div>
          <ol className="list-decimal pl-5 space-y-1 text-sm marker:text-lifted-green">
            {mod.subcategories.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      )}

      <div className="border-t border-white/10 pt-4">
        <button
          onClick={onComplete}
          disabled={mod.progress?.status === "complete"}
          className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="module-mark-complete"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          {mod.progress?.status === "complete" ? "Module complete" : "Mark module complete"}
        </button>
      </div>
    </div>
  );
}

function NotesTab({ slug }) {
  const [body, setBody] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/training/notes/${slug}`).then((r) => setBody(r.data.note?.body || ""));
  }, [slug]);

  const save = async () => {
    await api.put(`/training/notes/${slug}`, { body });
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  return (
    <div className="glass-panel p-5 space-y-3" data-testid="module-tab-content-notes">
      <div className="font-bebas tracking-[0.22em] text-[0.65rem] text-lifted-green">PRIVATE NOTES</div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={8}
        placeholder="Jot anything that'll help you remember…"
        className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
        data-testid="module-notes-body"
      />
      <div className="flex items-center gap-3">
        <button onClick={save} className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-2"
                data-testid="module-notes-save">
          <Save className="h-3.5 w-3.5" /> Save notes
        </button>
        {saved && <span className="text-xs text-lifted-green">Saved.</span>}
      </div>
    </div>
  );
}

function QATab({ slug }) {
  const [questions, setQuestions] = useState([]);
  const [text, setText] = useState("");
  const load = useCallback(async () => {
    const { data } = await api.get(`/training/qa/${slug}`);
    setQuestions(data.questions || []);
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const ask = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await api.post(`/training/qa/${slug}`, { body: text.trim() });
    setText("");
    load();
  };

  return (
    <div className="glass-panel p-5 space-y-4" data-testid="module-tab-content-qa">
      <div className="font-bebas tracking-[0.22em] text-[0.65rem] text-lifted-green">ASK A QUESTION</div>
      <form onSubmit={ask} className="flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What would you like a trainer to clarify?"
          className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
          data-testid="module-qa-input"
        />
        <button type="submit" className="btn-lifted !py-2 !px-4 text-xs" data-testid="module-qa-submit">Ask</button>
      </form>
      <div className="space-y-3">
        {questions.length === 0 && <div className="text-xs text-lifted-dim">Be the first to ask.</div>}
        {questions.map((q) => (
          <div key={q.id} className="border-t border-white/5 pt-3">
            <div className="text-sm text-lifted-text">{q.body}</div>
            <div className="text-[0.65rem] text-lifted-dim mt-0.5">— {q.asker_name} · {q.status}</div>
            {q.answer && (
              <div className="mt-2 pl-3 border-l-2 border-lifted-green/40 text-sm">
                <span className="text-lifted-green font-bebas tracking-[0.18em] text-[0.6rem]">TRAINER ↳ </span>
                {q.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ResourcesTab({ mod }) {
  const items = [
    { label: "Hero image", url: mod.hero_image_url },
    { label: "MP4 / Video", url: mod.video_url },
    { label: "PDF / Document", url: mod.pdf_url },
  ].filter((x) => x.url);
  return (
    <div className="glass-panel p-5 space-y-3" data-testid="module-tab-content-resources">
      <div className="font-bebas tracking-[0.22em] text-[0.65rem] text-lifted-green">MODULE RESOURCES</div>
      {items.length === 0 ? (
        <div className="text-xs text-lifted-dim">No resources attached yet.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.label}>
              <a href={it.url} target="_blank" rel="noreferrer" className="text-lifted-green underline text-sm">
                {it.label}
              </a>
            </li>
          ))}
        </ul>
      )}
      <div className="border-t border-white/10 pt-3 text-[0.65rem] text-lifted-dim">
        Announcements + classroom chat tabs coming soon.
      </div>
    </div>
  );
}
